import { Check, Close } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import {
	Autocomplete,
	Box,
	Grid2,
	IconButton,
	InputAdornment,
	Link,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { getUrlTypeIcon } from "../../../../../utils/iconUtils";

export interface UrlItem {
	id?: number;
	url: string;
	name?: string;
	type?: { id: number; label: string } | null;
	[key: string]: any;
}

export interface UrlTableProps {
	urls: UrlItem[];
	isVisible?: boolean;
	isAddingUrl?: boolean;
	addingUrl?: UrlItem | null;
	onAddNewUrl?: () => void;
	onConfirmAddUrl?: () => void;
	onCancelAddUrl?: () => void;
	onRemoveUrl?: (index: number) => void;
	onUrlChange?: (url: Partial<UrlItem>) => void;
	// Optional props for type selection
	showTypeColumn?: boolean;
	urlTypes?: Array<{ id: number; label: string }>;
	// Display mode
	displayMode?: boolean;
	// Header visibility
	showHeader?: boolean;
	// Styling
	tableWidth?: string;
	urlColumnWidth?: string;
	typeColumnWidth?: string;
	actionColumnWidth?: string;
	// Error handling
	hasFieldError?: (fieldPath: string) => boolean;
	getFieldError?: (fieldPath: string) => string | undefined;
	errorFieldPath?: string;
	// Additional handlers
	onFieldTouch?: () => void;
}

const UrlTable: React.FC<UrlTableProps> = ({
	urls,
	isVisible = true,
	isAddingUrl = false,
	addingUrl = null,
	onAddNewUrl = () => {},
	onConfirmAddUrl = () => {},
	onCancelAddUrl = () => {},
	onRemoveUrl = () => {},
	onUrlChange,
	showTypeColumn = false,
	urlTypes = [],
	displayMode = false,
	showHeader = true,
	tableWidth = "100%",
	urlColumnWidth = "660px",
	typeColumnWidth = "400px",
	actionColumnWidth = "300px",
	hasFieldError,
	getFieldError,
	errorFieldPath,
	onFieldTouch
}) => {
	const { t } = useTranslation();

	// Internal labels - not customizable
	const effectiveShowTypeColumn = displayMode ? true : showTypeColumn;
	const urlColumnLabel = t("urlTable.columns.url");
	const typeColumnLabel = t("urlTable.columns.type");
	const addUrlLabel = t("urlTable.actions.addUrl");
	const urlPlaceholder = t("urlTable.placeholders.enterUrl");
	const selectTypeLabel = t("urlTable.placeholders.selectType");

	if (!isVisible) return null;

	return (
		<Grid2 size={{ xs: 12, md: 12 }}>
			<TableContainer component={Paper} elevation={1} sx={{ width: tableWidth }}>
				<Table>
					{showHeader && (
						<TableHead>
							<TableRow>
								{effectiveShowTypeColumn && (
									<TableCell sx={{ width: typeColumnWidth, fontWeight: 600 }}>
										{typeColumnLabel}
									</TableCell>
								)}
								<TableCell
									sx={{
										width: effectiveShowTypeColumn ? "460px" : urlColumnWidth,
										fontWeight: 600
									}}
								>
									{urlColumnLabel}
								</TableCell>
								{!displayMode && (
									<TableCell
										align="center"
										sx={{ width: actionColumnWidth, fontWeight: 600 }}
									>
										<Link
											href="#"
											onClick={e => {
												e.preventDefault();
												onAddNewUrl();
											}}
										>
											{addUrlLabel}
										</Link>
									</TableCell>
								)}
							</TableRow>
						</TableHead>
					)}
					<TableBody>
						{/* Show existing URLs */}
						{urls.map((urlItem, urlIndex) => (
							<TableRow key={`url-${urlItem.url}-${urlIndex}`}>
								{effectiveShowTypeColumn && (
									<TableCell>
										<Typography variant="body1" color="text.secondary">
											<Box
												component="li"
												sx={{
													display: "flex",
													alignItems: "center",
													gap: 1
												}}
											>
												{urlItem.type && getUrlTypeIcon(urlItem.type.label)}
												{urlItem.type && t(`UrlTypes.${urlItem.type.label}`)}
											</Box>
										</Typography>
									</TableCell>
								)}
								<TableCell>
									<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
										<Link
											href={urlItem.url}
											target="_blank"
											variant="body1"
											color="secondary"
											sx={{
												flex: 1,
												textDecoration: "none",
												fontWeight: "medium",
												cursor: "pointer",
												border: "none",
												background: "none",
												padding: 0,
												"&:hover": {
													textDecoration: "underline"
												}
											}}
										>
											{urlItem.url}
										</Link>
									</Box>
								</TableCell>
								{!displayMode && (
									<TableCell align="center">
										<IconButton size="small" onClick={() => onRemoveUrl(urlIndex)}>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</TableCell>
								)}
							</TableRow>
						))}

						{/* Add new URL row */}
						{!displayMode && (isAddingUrl || urls.length === 0) && (
							<TableRow>
								{showTypeColumn && (
									<TableCell sx={{ paddingBottom: "40px" }}>
										<Autocomplete
											size="small"
											options={urlTypes}
											getOptionLabel={option => t(`UrlTypes.${option.label}`)}
											value={
												urlTypes.find(type => type.id === addingUrl?.type?.id) || null
											}
											onChange={(_, value) => {
												if (onUrlChange) {
													onUrlChange({ type: value });
												}
											}}
											renderOption={(props, option) => (
												<Box
													component="li"
													{...props}
													sx={{ display: "flex", alignItems: "center", gap: 1 }}
												>
													{getUrlTypeIcon(option.label)}
													{t(`UrlTypes.${option.label}`)}
												</Box>
											)}
											renderInput={params => (
												<TextField
													{...params}
													placeholder={selectTypeLabel}
													variant="outlined"
													slotProps={{
														input: {
															...params.InputProps,
															startAdornment: addingUrl?.type ? (
																<InputAdornment position="start">
																	{getUrlTypeIcon(addingUrl.type.label)}
																</InputAdornment>
															) : null
														}
													}}
												/>
											)}
										/>
									</TableCell>
								)}
								<TableCell sx={{ paddingBottom: "40px" }}>
									<TextField
										fullWidth
										placeholder={urlPlaceholder}
										value={addingUrl?.url || ""}
										onChange={e => {
											if (onUrlChange) {
												onUrlChange({
													url: e.target.value,
													name: e.target.value
												});
											}
										}}
										onBlur={() => {
											if (onFieldTouch) {
												onFieldTouch();
											}
										}}
										variant="outlined"
										size="small"
									/>
								</TableCell>
								<TableCell align="center" sx={{ paddingBottom: "40px" }}>
									<IconButton sx={{ mr: 1 }} onClick={onCancelAddUrl} size="small">
										<Close />
									</IconButton>
									<IconButton
										disabled={!addingUrl?.url || (showTypeColumn && !addingUrl?.type)}
										onClick={onConfirmAddUrl}
										size="small"
									>
										<Check />
									</IconButton>
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Error display */}
			{hasFieldError &&
				getFieldError &&
				errorFieldPath &&
				hasFieldError(errorFieldPath) && (
					<Typography color="error" variant="body2" sx={{ mt: 1, display: "block" }}>
						{getFieldError(errorFieldPath)}
					</Typography>
				)}
		</Grid2>
	);
};

export default UrlTable;
